"use client";

import { useEffect, useMemo, useState } from "react";
import { format, startOfMonth, subMonths, addMonths } from "date-fns";
import { ChevronLeft, ChevronRight } from "lucide-react";
import type { Matcher } from "react-day-picker";
import { Calendar } from "@/components/ui/calendar";
import { cn } from "@/lib/utils";
import {
  filterCalendarDayHoverClass,
  filterCalendarNavButtonClass,
  filterMobileCalendarDayClass,
  filterMobileCalendarDayDisabledClass,
  filterMobileCalendarDaySelectedClass,
} from "./filterStyles";


const singleMonthClassNames = {
  root: "mx-auto w-full",
  months: "w-full",
  month: "flex w-full flex-col gap-2",
  month_caption: "sr-only",
  nav: "hidden",
  month_grid: "w-full table-fixed border-collapse",
  weeks: "flex w-full flex-col gap-1",
  weekdays: "grid w-full grid-cols-7",
  weekday:
    "flex h-8 items-center justify-center text-xs font-medium text-gray-500",
  week: "grid w-full grid-cols-7",
  day: "h-10 p-0 text-center align-middle",
  outside: "[&>button]:hidden",
  disabled: "text-gray-300",
  today: "text-gray-600",
  selected: "",
};

interface MarketplaceSingleMonthCalendarProps {
  selected?: Date;
  onSelect?: (date: Date | undefined) => void;
  defaultMonth?: Date;
  disabled?: { before?: Date; after?: Date };
  /** Keeps 6 week rows so sheet height stays stable between months */
  fixedWeeks?: boolean;
  className?: string;
}

function toDisabledMatchers(
  disabled?: { before?: Date; after?: Date }
): Matcher | Matcher[] | undefined {
  if (!disabled) return undefined;

  const matchers: Matcher[] = [];
  if (disabled.before) matchers.push({ before: disabled.before });
  if (disabled.after) matchers.push({ after: disabled.after });

  if (matchers.length === 0) return undefined;
  return matchers.length === 1 ? matchers[0] : matchers;
}

export function MarketplaceSingleMonthCalendar({
  selected,
  onSelect,
  defaultMonth = new Date(),
  disabled,
  fixedWeeks = true,
  className,
}: MarketplaceSingleMonthCalendarProps) {
  const [month, setMonth] = useState(() =>
    startOfMonth(selected ?? defaultMonth)
  );

  useEffect(() => {
    setMonth(startOfMonth(selected ?? defaultMonth));
  }, [defaultMonth, selected]);

  const headerLabel = useMemo(() => format(month, "MMMM yyyy"), [month]);

  return (
    <div
      className={cn(
        "marketplace-single-month-calendar font-family-body",
        className
      )}>
      <div className="mb-2 flex items-center justify-between gap-3">
        <button
          type="button"
          onClick={() => setMonth((m) => subMonths(m, 1))}
          className={filterCalendarNavButtonClass}
          aria-label="Previous month">
          <ChevronLeft className="size-5" />
        </button>
        <span className="text-base font-semibold text-gray-900">
          {headerLabel}
        </span>
        <button
          type="button"
          onClick={() => setMonth((m) => addMonths(m, 1))}
          className="flex size-10 shrink-0 items-center justify-center rounded-full border border-gray-200 bg-white text-gray-700 hover:bg-gray-50"
          aria-label="Next month">
          <ChevronRight className="size-5" />
        </button>
      </div>

      <Calendar
        mode="single"
        numberOfMonths={1}
        month={month}
        onMonthChange={setMonth}
        hideNavigation
        showOutsideDays
        fixedWeeks={fixedWeeks}
        selected={selected}
        onSelect={onSelect}
        disabled={toDisabledMatchers(disabled)}
        className="w-full min-h-[17.5rem] bg-transparent p-0"
        classNames={singleMonthClassNames}
        components={{
          DayButton: ({ className, modifiers, ...props }) => (
            <button
              {...props}
              type="button"
              className={cn(
                "mx-auto flex h-10 w-full max-w-10 items-center justify-center p-0 shadow-none transition-colors",
                modifiers.disabled
                  ? filterMobileCalendarDayDisabledClass
                  : modifiers.selected
                    ? filterMobileCalendarDaySelectedClass
                    : cn(
                        filterMobileCalendarDayClass,
                        filterCalendarDayHoverClass,
                        "rounded-md bg-transparent"
                      ),
                className
              )}
            />
          ),
        }}
      />
    </div>
  );
}
